import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    industry: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5curevdcu97hjrn4400', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', industry: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-[5%]">
        <div className="mb-16">
          <div className="rounded-3xl overflow-hidden h-[400px] w-full mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/5b65113d6072df605e4383f4d553cda3.jpg"
              alt="Contact Us"
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              {t('contact.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('contact.subtitle')}
            </p>
            <p className="text-lg text-gray-600 mt-2">
              {t('contact.description')}
            </p>
          </div>
        </div>

        <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('contact.form.name')} *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all text-sm"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('contact.form.email')} *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all text-sm"
                placeholder="john@company.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('contact.form.company')}
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all text-sm"
                placeholder="Your Company"
              />
            </div>
            <div>
              <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('contact.form.industry')}
              </label>
              <select
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
              >
                <option value="">{t('contact.form.industryPlaceholder')}</option>
                <option value="healthcare">{t('contact.form.healthcare')}</option>
                <option value="education">{t('contact.form.education')}</option>
                <option value="retail">{t('contact.form.retail')}</option>
                <option value="other">{t('contact.form.other')}</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('contact.form.message')} *
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={6}
              maxLength={500}
              value={formData.message}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent outline-none transition-all resize-none text-sm"
              placeholder={t('contact.form.messagePlaceholder')}
            ></textarea>
            <div className="text-right text-xs text-gray-500 mt-1">
              {formData.message.length}/500 characters
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
              {t('contact.form.success')}
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {t('contact.form.error')}
            </div>
          )}

          <div className="flex items-center justify-center gap-5">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-10 py-4 bg-gray-900 text-white rounded-full font-semibold text-sm hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
            </button>
            <a
              href="mailto:marketing@globalpillar.pro"
              className="px-10 py-4 bg-transparent border-2 border-gray-900 text-gray-900 rounded-full font-semibold text-sm hover:bg-gray-50 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              {t('contact.email')}
            </a>
          </div>

          <div className="text-center mt-8 text-sm text-gray-600">
            <span>{t('contact.directEmail')} </span>
            <a href="mailto:marketing@globalpillar.pro" className="hover:text-[#14B8A6] transition-colors">
              marketing@globalpillar.pro
            </a>
          </div>
        </form>
      </div>
    </section>
  );
}
